import React, { useState, MouseEvent, useEffect } from 'react';
import {
    Button,
    FormControl,
    FormControlLabel,
    MenuItem,
    Popover,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import './UploadedFilter.scss';
import { useTranslation } from 'react-i18next';
import { Option } from '..';
import DebouncedInput from '../DebouncedInput';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { generateUIId } from '../../../services/elementHelperService';
import moment from 'moment';

interface UploadedFilterProps {
    data: any;
    onChange: (_params: any) => void;
    removeFilter: (_param: string) => void;
};

export enum UPLOADED_FILTER_OPTIONS {
    _DATE = 'within-the-last',
    _TIME = 'more-than',
    _BETWEEN = 'between',
};

type durationUnits = 'days' | 'weeks' | 'months' | 'years';
type timeDurationUnits = 'minutes' | 'hours';

export const periodUnits:durationUnits[] = ["days", "weeks", "months", "years"];
const timeUnits:timeDurationUnits[] = ["minutes", "hours"];

export const getQueryForOption = (data: any) => {
    if (!data) {
        return '';
    }
    switch (data.option) {
        case UPLOADED_FILTER_OPTIONS._DATE: {
            const dateFrom = moment().subtract(data.value, data.unit).format('YYYY-MM-DD');
            return ` AND created:[${dateFrom} TO ${moment().format('YYYY-MM-DD')}]`;
        }
        case UPLOADED_FILTER_OPTIONS._TIME: {
            const timeTo = moment().subtract(data.value, data.unit).utc().format('YYYY-MM-DD[T]HH:mm:ss');
            return ` AND created:[TO ${timeTo}]`;
        }
        case UPLOADED_FILTER_OPTIONS._BETWEEN: {
            return ` AND created:[${data.dateFrom.toString()} TO ${data.dateTo.toString()}]`;
        }
        default:
            return '';
    }
};

const UploadedFilter = React.memo(({ data, onChange, removeFilter }: UploadedFilterProps) => {
    const { t: translator } = useTranslation();
    const today = new Date();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRadioOption, setSelectedRadioOption] = useState<string>(UPLOADED_FILTER_OPTIONS._DATE);
    const [dateCount, setDATECount] = useState<string>("1");
    const [timeCount, setTIMECount] = useState<string>("1");
    const [dateUnit, setDATEUnit] = useState<durationUnits>(periodUnits[0]);
    const [timeUnit, setTIMEUnit] = useState<timeDurationUnits>(timeUnits[0]);
    const [betweenDateFrom, setBETWEENDateFrom] = useState<Date | null>(today);
    const [betweenDateTo, setBETWEENDateTo] = useState<Date | null>(today);

    useEffect(() => {
        if (data) {
            switch (data.option) {
                case UPLOADED_FILTER_OPTIONS._DATE:
                    setDATECount(data.value);
                    setDATEUnit(data.unit);
                    break;
                case UPLOADED_FILTER_OPTIONS._TIME:
                    setTIMECount(data.value);
                    setTIMEUnit(data.unit);
                    break;
                case UPLOADED_FILTER_OPTIONS._BETWEEN: {
                    setBETWEENDateFrom(moment(data.dateFrom).toDate());
                    setBETWEENDateTo(moment(data.dateTo).toDate());
                    break;
                }
            }
            setSelectedRadioOption(data.option);
        }
    }, [data]);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setSelectedRadioOption(value);
    };

    const open = Boolean(anchorEl);

    const getSelectedFilterValue = () => {
        let payload = {};
        switch (selectedRadioOption) {
            case UPLOADED_FILTER_OPTIONS._DATE:
                payload = {
                    unit: dateUnit,
                    value: dateCount,
                }
                break;
            case UPLOADED_FILTER_OPTIONS._TIME:
                payload = {
                    unit: timeUnit,
                    value: timeCount,
                }
                break;
            case UPLOADED_FILTER_OPTIONS._BETWEEN:
                payload = {
                    dateFrom: moment(betweenDateFrom).format('YYYY-MM-DD'),
                    dateTo: moment(betweenDateTo).format('YYYY-MM-DD'),
                }
                break;
        }
        return { ...payload, option: selectedRadioOption };
    };

    const getSelectedOptionLabel = () => {
        switch (selectedRadioOption) {
            case UPLOADED_FILTER_OPTIONS._DATE: {
                return `: Within the last ${dateCount} ${dateUnit}`;
            }
            case UPLOADED_FILTER_OPTIONS._TIME: {
                return `: More than ${timeCount} ${timeUnit} ago`;
            }
            case UPLOADED_FILTER_OPTIONS._BETWEEN: {
                return `: Between ${moment(betweenDateFrom).format('YYYY-MM-DD')} and ${moment(betweenDateTo).format('YYYY-MM-DD')}`;
            }
            default:
                return '';
        }
    };

    const applyChanges = () => {
        onChange(getSelectedFilterValue());
    };

    const clearIcon = (
        <ClearIcon
            className="clear-search-icon"
            fontSize="small"
            color="disabled"
            onClick={() => {
                removeFilter(Option._UPLOADED);
            }}
        />
    );

    return (
        <div>
            <Button
                variant="contained"
                onClick={handleClick}
                className="menu-btn no-shadow"
                endIcon={clearIcon}
            >
                <b>{translator('COMMON_UPLOADED')}</b>
                {getSelectedOptionLabel()}
            </Button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'popover-uploaded-content' }}
            >
                <FormControl fullWidth>
                    <RadioGroup
                        defaultValue="responsive"
                        value={selectedRadioOption}
                        onChange={handleRadioChange}
                    >
                        <FormControlLabel
                            className="radio-row"
                            control={<Radio style={{ color: '#126CFC' }} />}
                            label="Within the last"
                            value={UPLOADED_FILTER_OPTIONS._DATE}
                        />
                        {selectedRadioOption === UPLOADED_FILTER_OPTIONS._DATE &&
                            <div className="box1">
                                <DebouncedInput
                                    id="date-count"
                                    type="number"
                                    value={dateCount}
                                    onChange={setDATECount}
                                />
                                <Select
                                    variant="outlined"
                                    value={dateUnit}
                                    style={{ height: 42 }}
                                    onChange={(e: any) => setDATEUnit(e.target.value)}>
                                    {periodUnits.map((item: string, index: number) => (
                                        <MenuItem value={item} key={index}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        }
                        <FormControlLabel
                            className="radio-row"
                            control={<Radio style={{ color: '#126CFC' }} />}
                            label="More than"
                            value={UPLOADED_FILTER_OPTIONS._TIME}
                        />
                        {selectedRadioOption === UPLOADED_FILTER_OPTIONS._TIME &&
                            <div className="box1">
                                <DebouncedInput
                                    id="time-count"
                                    type="number"
                                    value={timeCount}
                                    onChange={setTIMECount}
                                />
                                <Select
                                    variant="outlined"
                                    value={timeUnit}
                                    style={{ height: 42 }}
                                    onChange={(e: any) => setTIMEUnit(e.target.value)}>
                                    {timeUnits.map((item: string, index: number) => (
                                        <MenuItem value={item} key={index}>
                                            {`${item} ago`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        }
                        <FormControlLabel
                            className="radio-row"
                            control={<Radio style={{ color: '#126CFC' }} />}
                            label="Between"
                            value={UPLOADED_FILTER_OPTIONS._BETWEEN}
                        />
                        {selectedRadioOption === UPLOADED_FILTER_OPTIONS._BETWEEN &&
                            <div className="box2">
							    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <KeyboardDatePicker
                                        disableToolbar
                                        variant="inline"
                                        format="dd/MM/yyyy"
                                        margin="normal"
                                        id="date-picker-inline"
                                        value={betweenDateFrom}
                                        onChange={setBETWEENDateFrom}
                                        TextFieldComponent={(props) => <TextField {...props} variant="outlined" />}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        key={generateUIId()}
                                    />
                                    <div className="joining-text">to</div>
                                    <KeyboardDatePicker
                                        disableToolbar
                                        variant="inline"
                                        format="dd/MM/yyyy"
                                        margin="normal"
                                        id="date-picker-inline"
                                        value={betweenDateTo}
                                        onChange={setBETWEENDateTo}
                                        TextFieldComponent={(props) => <TextField {...props} variant="outlined" />}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        key={generateUIId()}
                                    />
                                </MuiPickersUtilsProvider>
                            </div>
                        }
                    </RadioGroup>
                </FormControl>

                <div className="apply-button">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={applyChanges}
                        className="no-shadow"
                    >
                        {translator('COMMON_UPDATE')}
                    </Button>
                </div>
            </Popover>
        </div>
    );
});

export default UploadedFilter;
